import { Button } from '@/components/ui/button';
import { ContentDescriptionEditable } from '@/components/widget/content-description-editable';
import { CreateTemplateDialog } from '@/components/explore/create-template-dialog';
import { usePinReference, useReadFileText } from '@/hooks';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import type { FileContent, FileTextRead, FileTextReadReason } from '@vivport/backend/types';
import { renderAsync } from 'docx-preview';
import { ExternalLink, FileText, LayoutTemplate, Pin } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

interface DetailsFileProps {
  content: FileContent;
  onDescriptionSave: (description: string) => Promise<void>;
  isRegenerating: boolean;
}

function shouldParseAsJson(input: { mimeType?: string; uri: string; body: string }): boolean {
  const mimeType = input.mimeType?.toLowerCase() ?? '';
  if (mimeType.includes('application/json') || mimeType.endsWith('+json')) {
    return true;
  }
  if (input.uri.toLowerCase().endsWith('.json')) {
    return true;
  }
  const trimmed = input.body.trimStart();
  return trimmed.startsWith('{') || trimmed.startsWith('[');
}

function normalizeMimeType(mimeType?: string): string | undefined {
  if (!mimeType?.trim()) {
    return undefined;
  }
  return mimeType.toLowerCase().split(';')[0].trim();
}

function isPdfMimeType(mimeType?: string): boolean {
  return normalizeMimeType(mimeType) === 'application/pdf';
}

// Modern .docx only: the server-side extractor supports exactly this MIME, so legacy
// application/msword keeps the unsupported message rather than promising a preview.
function isWordDocumentMimeType(mimeType?: string): boolean {
  return (
    normalizeMimeType(mimeType) ===
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  );
}

function isRasterImageMimeType(mimeType?: string): boolean {
  const m = normalizeMimeType(mimeType);
  if (!m || !m.startsWith('image/')) {
    return false;
  }
  // SVG is treated as text/XML in `isTextPreviewMimeType` for source preview.
  return m !== 'image/svg+xml';
}

function isTextPreviewMimeType(mimeType?: string): boolean {
  if (!mimeType) {
    return false;
  }
  const normalized = mimeType.toLowerCase().split(';')[0].trim();
  if (normalized.startsWith('text/')) {
    return true;
  }
  if (
    normalized === 'application/json' ||
    normalized.endsWith('+json') ||
    normalized === 'application/xml' ||
    normalized.endsWith('+xml') ||
    normalized === 'application/javascript' ||
    normalized === 'application/x-javascript' ||
    normalized === 'application/typescript' ||
    normalized === 'application/x-www-form-urlencoded' ||
    normalized === 'image/svg+xml'
  ) {
    return true;
  }
  return false;
}

type FilePreviewMode = 'pdf' | 'image' | 'text-body' | 'word-document' | 'none';

function DetailsStoredFile({ content: file, uri, onDescriptionSave, isRegenerating }: DetailsFileProps & { uri: string }) {
  const [body, setBody] = useState<string | null>(null);
  const docxContainerRef = useRef<HTMLDivElement | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [previewUnavailableReason, setPreviewUnavailableReason] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [useAsTemplateOpen, setUseAsTemplateOpen] = useState(false);
  const fileUrl = useMemo(() => `/api/files/file/${encodeURIComponent(uri)}`, [uri]);

  const previewMode = useMemo((): FilePreviewMode => {
    if (isPdfMimeType(file.mimeType)) {
      return 'pdf';
    }
    if (isRasterImageMimeType(file.mimeType)) {
      return 'image';
    }
    if (isWordDocumentMimeType(file.mimeType)) {
      return 'word-document';
    }
    // Unknown MIME: try a text fetch and decide from `Content-Type` (same as before).
    if (!file.mimeType?.trim()) {
      return 'text-body';
    }
    if (isTextPreviewMimeType(file.mimeType)) {
      return 'text-body';
    }
    return 'none';
  }, [file.mimeType]);

  useEffect(() => {
    let cancelled = false;

    const loadFile = async () => {
      setIsLoading(true);
      setLoadError(null);
      setPreviewUnavailableReason(null);
      setBody(null);
      docxContainerRef.current?.replaceChildren();
      if (previewMode === 'pdf' || previewMode === 'image') {
        setIsLoading(false);
        return;
      }
      if (previewMode === 'none') {
        setPreviewUnavailableReason('Binary file preview is not supported. Use "Open raw file" to download or view it.');
        setIsLoading(false);
        return;
      }
      if (previewMode === 'word-document') {
        try {
          const response = await fetch(fileUrl);
          if (!response.ok) {
            throw new Error(`Failed to load document (${response.status} ${response.statusText})`);
          }
          const blob = await response.blob();
          if (cancelled) {
            return;
          }
          const container = docxContainerRef.current;
          if (!container) {
            throw new Error('Document preview container is unavailable');
          }
          container.replaceChildren();
          await renderAsync(blob, container, undefined, {
            inWrapper: true,
            breakPages: true,
            renderHeaders: true,
            renderFooters: true,
            renderFootnotes: true,
            useBase64URL: true,
          });
        } catch (error) {
          if (!cancelled) {
            const message = error instanceof Error ? error.message : 'Unknown document render error';
            setLoadError(message);
          }
        } finally {
          if (!cancelled) {
            setIsLoading(false);
          }
        }
        return;
      }
      try {
        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error(`Failed to load file body (${response.status} ${response.statusText})`);
        }
        const responseMimeType = response.headers.get('content-type')?.toLowerCase() ?? undefined;
        if (responseMimeType && !isTextPreviewMimeType(responseMimeType)) {
          if (!cancelled) {
            setPreviewUnavailableReason(
              'Binary file preview is not supported. Use "Open raw file" to download or view it.'
            );
          }
          return;
        }
        const text = await response.text();
        if (!cancelled) {
          setBody(text);
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : 'Unknown file load error';
          setLoadError(message);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadFile();
    return () => {
      cancelled = true;
    };
  }, [fileUrl, previewMode]);

  const jsonView = useMemo(() => {
    if (!body || !shouldParseAsJson({ mimeType: file.mimeType, uri, body })) {
      return null;
    }
    try {
      const parsed = JSON.parse(body);
      return { formatted: JSON.stringify(parsed, null, 2), parseError: null as string | null };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Invalid JSON payload';
      return { formatted: body, parseError: message };
    }
  }, [body, file.mimeType, uri]);

  return (
    <div data-testid="details-file" className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <span>MIME: {file.mimeType?.trim() || 'unknown'}</span>
        <span>Size: {typeof file.size === 'number' ? `${file.size.toLocaleString()} bytes` : 'unknown'}</span>
        <Button asChild variant="outline" size="sm">
          <a href={fileUrl} target="_blank" rel="noreferrer">
            <ExternalLink className="mr-1 h-3.5 w-3.5" />
            Open raw file
          </a>
        </Button>
        <Button variant="outline" size="sm" onClick={() => setUseAsTemplateOpen(true)}>
          <LayoutTemplate className="mr-1 h-3.5 w-3.5" />
          Use as template
        </Button>
      </div>
      <CreateTemplateDialog
        open={useAsTemplateOpen}
        onOpenChange={setUseAsTemplateOpen}
        prefilledSourceFileId={file.id}
        prefilledSourceIsDocx={isWordDocumentMimeType(file.mimeType)}
      />

      <div className="flex flex-col gap-2">
        <h3 className="font-medium">Description</h3>
        <ContentDescriptionEditable
          description={file.description || ''}
          onSave={onDescriptionSave}
          disabled={isRegenerating}
          placeholder={isRegenerating ? 'Regeneration currently in progress' : 'Click to add description'}
        />
      </div>

      <div className="flex flex-col gap-2 pb-12">
        <h3 className="mb-2 font-medium">{jsonView ? 'JSON preview' : 'File preview'}</h3>
        {isLoading ? <p className="text-sm text-muted-foreground">Loading file body...</p> : null}
        {previewUnavailableReason ? <p className="text-sm text-muted-foreground">{previewUnavailableReason}</p> : null}
        {loadError ? <p className="text-sm text-destructive">Unable to load file body: {loadError}</p> : null}
        {previewMode === 'pdf' ? (
          <iframe
            title={`PDF preview: ${file.title || uri}`}
            src={fileUrl}
            className="h-[min(70vh,720px)] w-full rounded-md border bg-muted/40"
          />
        ) : null}
        {previewMode === 'image' ? (
          <div className="overflow-auto rounded-md border bg-muted/40 p-2">
            <img
              src={fileUrl}
              alt={file.title || 'File preview'}
              className="mx-auto max-h-[min(70vh,720px)] w-auto max-w-full object-contain"
            />
          </div>
        ) : null}
        {previewMode === 'word-document' ? (
          <div className="max-h-[min(70vh,720px)] overflow-auto rounded-md border bg-muted/40">
            <div ref={docxContainerRef} />
          </div>
        ) : null}
        {!isLoading && !loadError && body !== null && previewMode === 'text-body' ? (
          <>
            {jsonView?.parseError ? (
              <p className="text-xs text-destructive">JSON parse error: {jsonView.parseError}</p>
            ) : null}
            <pre className="text-xs whitespace-pre-wrap break-all">
              {jsonView?.formatted ?? body}
            </pre>
          </>
        ) : null}
      </div>
    </div>
  );
}

const FILE_TEXT_REASON_MESSAGES: Record<Exclude<FileTextReadReason, 'OK'>, string> = {
  UNSUPPORTED_TYPE: 'This file type has no readable text.',
  TOO_LARGE: 'Too large to extract text.',
  NO_TEXT_FOUND: 'No text found in this file.',
};

function DetailsReferenceFile({ content: file, onDescriptionSave, isRegenerating }: DetailsFileProps) {
  const readText = useReadFileText();
  const pin = usePinReference();
  const [loadedText, setLoadedText] = useState<string | null>(null);
  const [read, setRead] = useState<FileTextRead | null>(null);

  const loadSlice = (startChar: number) => {
    readText.mutate(
      { contentId: file.id, startChar },
      {
        onSuccess: (result) => {
          setRead(result);
          if (result.reason === 'OK' && result.text !== null) {
            setLoadedText((previous) => (startChar === 0 || previous === null ? result.text : previous + result.text));
          }
        },
        onError: (error: unknown) => toast.error(getAxiosErrorDisplayMessage(error, 'Failed to read file text.')),
      }
    );
  };

  const handlePin = () => {
    const title = file.title || `File ${file.id}`;
    if (!window.confirm(`Pin a copy of '${title}' into Vivport storage? The SharePoint reference stays unchanged.`)) {
      return;
    }
    pin.mutate(file.id, {
      onSuccess: (result) => toast.success(`Pinned '${result.title}' as ${result.pinnedCode}.`),
      onError: (error: unknown) => toast.error(getAxiosErrorDisplayMessage(error, 'Failed to pin a copy.')),
    });
  };

  const loadedChars = loadedText?.length ?? 0;
  const moreAvailable = read !== null && read.reason === 'OK' && loadedChars < read.totalChars;

  return (
    <div data-testid="details-file" className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <span>Source: {file.provider === 'SERVER_SHARE' ? 'Server share' : 'SharePoint'}</span>
        <span>Path: {file.providerPath ?? 'unknown'}</span>
        <span>MIME: {file.mimeType?.trim() || 'unknown'}</span>
        <span>Size: {typeof file.size === 'number' ? `${file.size.toLocaleString()} bytes` : 'unknown'}</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={readText.isPending}
          onClick={() => loadSlice(0)}
        >
          <FileText className="mr-1 h-3.5 w-3.5" />
          {read === null ? 'Open text' : 'Reload text'}
        </Button>
        {file.webUrl ? (
          <Button asChild variant="outline" size="sm">
            <a href={file.webUrl} target="_blank" rel="noreferrer">
              <ExternalLink className="mr-1 h-3.5 w-3.5" />
              Open in SharePoint
            </a>
          </Button>
        ) : null}
        <Button type="button" variant="outline" size="sm" disabled={pin.isPending} onClick={handlePin}>
          <Pin className="mr-1 h-3.5 w-3.5" />
          Pin a copy
        </Button>
      </div>

      {read !== null ? (
        <div className="flex flex-col gap-2">
          <h3 className="font-medium">Text</h3>
          {read.reason === 'OK' && loadedText !== null ? (
            <>
              <pre className="max-h-[32rem] overflow-auto whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-xs">
                {loadedText}
              </pre>
              <p className="text-xs text-muted-foreground">
                Showing {loadedChars.toLocaleString()} of {read.totalChars.toLocaleString()} characters.
                {moreAvailable ? (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto p-0 pl-2"
                    disabled={readText.isPending}
                    onClick={() => loadSlice(loadedChars)}
                  >
                    Read more
                  </Button>
                ) : null}
              </p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              {read.reason === 'OK' ? FILE_TEXT_REASON_MESSAGES.NO_TEXT_FOUND : FILE_TEXT_REASON_MESSAGES[read.reason]}
            </p>
          )}
        </div>
      ) : null}

      <div className="flex flex-col gap-2">
        <h3 className="font-medium">Description</h3>
        <ContentDescriptionEditable
          description={file.description || ''}
          onSave={onDescriptionSave}
          disabled={isRegenerating}
          placeholder={isRegenerating ? 'Regeneration currently in progress' : 'Click to add description'}
        />
      </div>

    </div>
  );
}

export function DetailsFile(props: DetailsFileProps) {
  if (props.content.uri === null) {
    return <DetailsReferenceFile {...props} />;
  }
  return <DetailsStoredFile {...props} uri={props.content.uri} />;
}
